/**
 * CR327: Preview Stability Debug Counters
 * 
 * Dev-only panel showing counters for:
 * - Spec resolution calls
 * - Preview renderer remounts
 * - Sample deal fetches
 * 
 * Used to verify that editing doesn't cause excessive re-renders or refetches.
 */

import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Database, RefreshCw, Zap } from "lucide-react";

// Global counters (persist across component instances)
const globalCounters = {
  specResolves: 0,
  previewRemounts: 0,
  sampleFetches: 0,
};

// Subscribers for counter updates
const subscribers = new Set<() => void>();

function notifySubscribers() {
  subscribers.forEach((fn) => fn());
}

/**
 * Increment a counter (call from relevant code paths)
 */
export function incrementPreviewCounter(
  counter: "specResolves" | "previewRemounts" | "sampleFetches"
) {
  globalCounters[counter]++;
  notifySubscribers();
}

/**
 * Reset all counters (for testing)
 */
export function resetPreviewCounters() {
  globalCounters.specResolves = 0;
  globalCounters.previewRemounts = 0;
  globalCounters.sampleFetches = 0;
  notifySubscribers();
}

/**
 * Get current counter values
 */
export function getPreviewCounters() {
  return { ...globalCounters };
}

interface PreviewStabilityCountersProps {
  /** Additional class name */
  className?: string;
  /** Show only in dev mode (default: true) */
  devOnly?: boolean;
}

/**
 * Debug panel displaying preview stability counters.
 * Only renders in development mode by default.
 */
export function PreviewStabilityCounters({
  className = "",
  devOnly = true,
}: PreviewStabilityCountersProps) {
  const isDev = import.meta.env.DEV;
  const [, setTick] = useState(0);
  
  // Subscribe to counter updates for re-render
  useEffect(() => {
    const handler = () => setTick((t) => t + 1);
    subscribers.add(handler);
    return () => {
      subscribers.delete(handler);
    };
  }, []);
  
  if (devOnly && !isDev) {
    return null;
  }
  
  const { specResolves, previewRemounts, sampleFetches } = globalCounters;
  
  return (
    <div
      className={`flex items-center gap-3 text-[10px] font-mono text-muted-foreground bg-muted/50 px-2 py-1 rounded ${className}`}
      data-testid="preview-stability-counters"
    >
      <span className="font-medium text-foreground">CR327 Counters:</span>
      
      <div className="flex items-center gap-1" title="Spec resolution calls">
        <Zap className="h-3 w-3" />
        <span>Spec:</span>
        <Badge variant="outline" className="h-4 px-1 text-[9px]">
          {specResolves}
        </Badge>
      </div>
      
      <div className="flex items-center gap-1" title="Preview renderer remounts">
        <RefreshCw className="h-3 w-3" />
        <span>Remounts:</span>
        <Badge variant="outline" className="h-4 px-1 text-[9px]">
          {previewRemounts}
        </Badge>
      </div>
      
      <div className="flex items-center gap-1" title="Sample deal fetches">
        <Database className="h-3 w-3" />
        <span>Fetches:</span>
        <Badge variant="outline" className="h-4 px-1 text-[9px]">
          {sampleFetches}
        </Badge>
      </div>
      
      <button
        onClick={resetPreviewCounters}
        className="text-[9px] underline hover:no-underline ml-2"
        title="Reset counters"
      >
        Reset
      </button>
    </div>
  );
}

export default PreviewStabilityCounters;
